import { useMutation } from '@tanstack/react-query';
import { Col, DatePicker, Flex, Form, Modal, Row, Segmented } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { PlantApi } from 'apis/PlantApis';
import { RewardCalculationApis } from 'apis/RewardCalculationApis';
import { SaleTeamApi } from 'apis/SaleTeamApi';
import { SelectForm } from 'components';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { useUser } from 'contexts/UserProvider';
import { Dayjs } from 'dayjs';
import { IFilterTeamSaleCb } from 'interfaces/TeamSale/TeamSale';
import { useEffect, useMemo, useState } from 'react';
import { toastError, toastSuccess } from 'utils/constants/functions';

const PLANT_OPTION = 'Chi nhánh';
const TEAM_OPTION = 'Đội';

const BonusModal = ({
  open,
  onClose,
  refresh,
}: {
  open: boolean;
  onClose: () => void;
  refresh: () => void;
}) => {
  const [form] = Form.useForm();
  const { user } = useUser();
  const [value, setValue] = useState<string>(PLANT_OPTION);
  const [plants, setPlants] = useState<DefaultOptionType[]>([]);
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [teamSaleData, setTeamSaleData] =
    useState<ILazySelectData>(initLazySelectData);
  const teamOptions = useMemo(
    () =>
      ((user as any)?.teamSales ?? []).map((item: any) => ({
        label: item.subId + ' - ' + item.name,
        value: item.subId,
      })) ?? [],
    [user],
  );
  const defaultFilter = useMemo(() => {
    const userAny = user as any;

    if (user?.isCN)
      return {
        plantId: userAny?.plantCode,
      };

    if (user?.isSale) {
      const teamSale = userAny?.teamSales?.[0];

      return {
        plantId: userAny?.plantCode,
        teamSalesId: teamSale?.id,
        teamSalesSubId: teamSale?.subId,
        teamSalesName: teamSale?.name,
      };
    }

    return {};
  }, [user]);

  const { mutate: getAllPlant, isPending: getAllPlantLoading } = useMutation({
    mutationFn: () => PlantApi.getPlantById(),
    onSuccess: (data: any) => {
      const items = data.data?.getPlant?.items || [];

      if (!items?.length) return setPlants([]);

      const plants = items.map((item: any) => ({
        label: item.id,
        value: item.id,
      }));

      setPlants(plants);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { isPending: getTeamSaleLoading, mutate: getTeamSales } = useMutation({
    mutationFn: (params: IFilterTeamSaleCb) =>
      SaleTeamApi.getAllTeamSaleCbSubSort(params),
    onSuccess: (res) => {
      if (res?.data?.getTeamSales) {
        const items = res.data?.getTeamSales?.items ?? [],
          totalItems = res.data?.getTeamSales?.pageInfo?.totalItems ?? 0;

        setTeamSaleData({
          options: items.map((item: any) => ({
            label: item.subId + ' - ' + item.name,
            value: `${item.id}.${item.subId}.${item.plantId}`,
          })),
          totalItems: totalItems,
        });
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const handleSuccess = (res: any) => {
    if (!res?.data) {
      toastError(res?.errors[0]?.message || 'Tính thưởng thất bại');
    } else {
      toastSuccess('Tính thưởng thành công');
      refresh();
      onClose();
    }
  };

  const handleError = (error: any) => {
    toastError(error?.message || 'Tính thưởng thất bại');
  };

  const {
    mutate: calculateRewardForTeam,
    isPending: calculateRewardForTeamLoading,
  } = useMutation({
    mutationFn: RewardCalculationApis.calculateRewardForTeam,
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const {
    mutate: calculateRewardForPlant,
    isPending: calculateRewardForPlantLoading,
  } = useMutation({
    mutationFn: RewardCalculationApis.calculateRewardForPlant,
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const handleChange = (value: string) => {
    setValue(value);
    form.resetFields();
  };

  const handleTeamSaleScrollChange = (params: ILazySelectParams) => {
    const plant = form.getFieldValue('plantId');
    const teamSaleParams: IFilterTeamSaleCb = {
      ...params,
      plantId: plant || defaultFilter.plantId,
    };

    getTeamSales(teamSaleParams);
  };

  const validate = async () => {
    try {
      await form.validateFields();
      return true;
    } catch (error) {
      error;
    }

    return false;
  };

  const handleOk = () => {
    if (!validate()) return;

    const values = form.getFieldsValue();
    values.fromDate = values.fromDate?.format('YYYY-MM-DD');
    values.toDate = values.toDate?.format('YYYY-MM-DD');

    if (value === PLANT_OPTION) {
      calculateRewardForPlant(values);
    } else {
      calculateRewardForTeam(values);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  useEffect(() => {
    getAllPlant();
  }, [getAllPlant]);

  return (
    <Modal
      open={open}
      title="Tính thưởng"
      okText="Tính thưởng"
      cancelText="Hủy"
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={
        calculateRewardForTeamLoading || calculateRewardForPlantLoading
      }
    >
      <Flex justify="center">
        <Segmented
          value={value}
          style={{ width: '200px' }}
          options={[PLANT_OPTION, TEAM_OPTION]}
          block
          onChange={handleChange}
        />
      </Flex>
      <Form className="mt-6" layout="vertical" form={form}>
        <Row gutter={[12, 12]}>
          {(value === PLANT_OPTION && (
            <Col span={24} className="h-[80px]">
              <Form.Item
                label="Chi nhánh"
                name="plantId"
                rules={[
                  { required: true, message: 'Chi nhánh không được để trống' },
                ]}
              >
                <SelectForm
                  size="large"
                  loading={getAllPlantLoading}
                  options={plants}
                  disabled={!(user?.isAdmin || user?.isHO)}
                  placeholder="Chọn chi nhánh"
                />
              </Form.Item>
            </Col>
          )) || (
            <Col span={24} className="h-[80px]">
              <Form.Item
                label="Đội bán hàng"
                name="teamSalesId"
                rules={[
                  {
                    required: true,
                    message: 'Đội bán hàng không được để trống',
                  },
                ]}
              >
                {user?.isSale ? (
                  <SelectForm
                    disabled
                    placeholder="Chọn đội"
                    options={teamOptions}
                  />
                ) : (
                  <LazySelectForm
                    customeSize={20}
                    data={teamSaleData}
                    placeholder="Chọn đội"
                    loading={getTeamSaleLoading}
                    onSelectScrollChange={handleTeamSaleScrollChange}
                  />
                )}
              </Form.Item>
            </Col>
          )}
          <Col span={12} className="h-[80px]">
            <Form.Item
              label="Từ ngày"
              name="fromDate"
              rules={[
                { required: true, message: 'Từ ngày không được để trống' },
              ]}
            >
              <DatePicker
                placeholder="Chọn ngày"
                format={'DD/MM/YYYY'}
                maxDate={fromDate || undefined}
                size="large"
                className="w-full"
                onChange={(date) => setToDate(date)}
              />
            </Form.Item>
          </Col>
          <Col span={12} className="h-[80px]">
            <Form.Item
              label="Đến ngày"
              name="toDate"
              rules={[
                { required: true, message: 'Đến ngày không được để trống' },
              ]}
            >
              <DatePicker
                placeholder="Chọn ngày"
                format={'DD/MM/YYYY'}
                minDate={toDate || undefined}
                size="large"
                className="w-full"
                onChange={(date) => setFromDate(date)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default BonusModal;
